/**
 * Example content for each built-in template, plus the short human labels the
 * template picker shows.
 *
 * These are FIELDS, not HTML: previews are rendered through the real
 * SlideRenderer.buildHtml, so a change to a template shows up in the picker
 * automatically and a preview can never drift from what the deck will look
 * like. (public/dev-templates.html predates this and still hardcodes markup.)
 *
 * Image fields are left unset on purpose – every template already falls back to
 * its placeholder box, and a preview should not spend an Unsplash call.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.TemplateSamples = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    const SAMPLES = {
        'title': {
            label: 'Title',
            blurb: 'Opening slide. Used once, for slide 1.',
            fields: { heading: 'The Water Cycle', subtitle: 'How water moves around the Earth' },
        },
        'title-image': {
            label: 'Title with photo',
            blurb: 'Opening slide with a full-bleed background photo.',
            fields: { heading: 'The Water Cycle', subtitle: 'How water moves around the Earth' },
        },
        'content': {
            label: 'Bullet points',
            blurb: 'Heading plus a list. The general-purpose fallback.',
            fields: {
                heading: 'Stages of the Water Cycle',
                bullets: [
                    'Evaporation – solar energy turns surface water to vapour',
                    'Condensation – vapour cools and forms clouds',
                    'Precipitation – water falls as rain, sleet or snow',
                    'Collection – water gathers in rivers and oceans',
                ],
            },
        },
        'two-column': {
            label: 'Two columns',
            blurb: 'Side-by-side comparison or two parallel ideas, text only.',
            fields: {
                heading: 'Evaporation vs Condensation',
                left_heading: 'Evaporation',
                left_bullets: ['Liquid becomes vapour', 'Driven by solar radiation', 'Happens at the surface'],
                right_heading: 'Condensation',
                right_bullets: ['Vapour becomes liquid', 'Happens as air cools', 'Forms clouds and fog'],
            },
        },
        'comparison-diagram': {
            label: 'Comparison + diagram',
            blurb: 'Two short columns with a supporting diagram underneath.',
            fields: {
                heading: 'TCP/IP vs OSI',
                left_heading: 'TCP/IP',
                left_bullets: ['4 layers', 'Practical internet model'],
                right_heading: 'OSI',
                right_bullets: ['7 layers', 'Theoretical reference model'],
            },
        },
        'process-flow': {
            label: 'Process steps',
            blurb: 'A short numbered sequence, for when the order is the point.',
            fields: {
                heading: 'Stages of the Water Cycle',
                steps: [
                    'Evaporation: Solar energy converts surface water to vapour',
                    'Condensation: Vapour cools and forms clouds',
                    'Precipitation: Water falls back to the ground',
                    'Collection: Water gathers in rivers and oceans',
                ],
            },
        },
        'quote': {
            label: 'Quote',
            blurb: 'A single memorable quote. At most once per deck.',
            fields: {
                quote_text: 'In every walk with nature one receives far more than he seeks.',
                attribution: 'John Muir',
            },
        },
        'chapter': {
            label: 'Chapter break',
            blurb: 'Marks a transition between major parts of a long deck.',
            fields: { chapter_number: '02', chapter_title: 'Precipitation', chapter_subtitle: 'How water returns to the ground' },
        },
        'image-text': {
            label: 'Photo + paragraph',
            blurb: 'Heading, a short paragraph, and a full-bleed photo.',
            fields: {
                heading: 'Cloud Formation',
                body: 'As warm air rises it cools, and the water vapour it carries condenses onto tiny particles of dust to form clouds.',
            },
        },
        'timeline': {
            label: 'Timeline',
            blurb: 'Dated events or ordered steps, best with 4–6 items.',
            fields: {
                heading: 'A History of Weather Forecasting',
                events: [
                    '1854: First national storm warning service',
                    '1922: Numerical forecasting proposed',
                    '1950: First computer weather forecast',
                    '1960: First weather satellite launched',
                ],
            },
        },
        'definition': {
            label: 'Definition',
            blurb: 'One key term with its formal definition and an example.',
            fields: {
                term: 'Transpiration',
                definition: 'The process by which water moves through a plant and evaporates from its leaves, stems and flowers.',
                example: 'A large oak can transpire over 150,000 litres of water in a single year.',
            },
        },
        'term-grid': {
            label: 'Key vocabulary grid',
            blurb: 'A row of 2–4 terms, each with a small diagram and definition.',
            fields: {
                heading: 'Key Terms',
                items: [
                    { term: 'Evaporation', definition: 'Liquid water turning into vapour.' },
                    { term: 'Condensation', definition: 'Vapour cooling back into liquid.' },
                    { term: 'Precipitation', definition: 'Water falling from clouds to the ground.' },
                ],
            },
        },
        'concept': {
            label: 'Concept',
            blurb: 'One named concept with its key fact, an example and a diagram.',
            fields: {
                category: 'Earth Science',
                heading: 'Latent Heat',
                body: 'The energy absorbed or released when a substance changes state, without any change in temperature.',
                key_fact: '$Q = mL$',
                example_text: 'Evaporating 1 kg of water absorbs about 2,260 kJ of energy from its surroundings.',
                callout_kind: 'example',
            },
        },
        'icon-cards': {
            label: 'Icon cards',
            blurb: 'Three ideas with symbols, for things no photo shows.',
            fields: {
                heading: 'Why is the River Important?',
                items: [
                    { label: 'Drinking', text: 'People need fresh river water to drink and stay healthy.', icon: 'droplet' },
                    { label: 'Washing', text: 'People used the river to wash themselves and their clothes.', icon: 'bath' },
                    { label: 'Growing', text: 'Farmers use the water to help their crops grow.', icon: 'sprout' },
                ],
            },
        },
        'hook': {
            label: 'Hook question',
            blurb: 'The puzzle the rest of the deck answers. Near the front.',
            fields: {
                heading: 'A Puzzling Journey',
                question: 'How does water get from the sea all the way back up to the clouds?',
            },
        },
        'recap': {
            label: 'Recap',
            blurb: 'Closing slide: what to leave with, and one big idea.',
            fields: {
                heading: 'What We Have Learned',
                intro: 'You have learned so much about the water cycle today:',
                takeaways: [
                    'Water moves in a never-ending cycle',
                    'The Sun provides the energy that drives it',
                    'Clouds form when vapour cools and condenses',
                ],
                callout_kind: 'remember',
                callout_text: 'The same water has been going round the Earth for billions of years.',
            },
        },
        'photo-process': {
            label: 'Picture sequence',
            blurb: 'Three stages in order, each with its own picture.',
            fields: {
                heading: 'From Sea to Cloud',
                steps: [
                    { label: 'Evaporation', detail: 'The Sun warms the sea and water rises.' },
                    { label: 'Condensation', detail: 'Vapour cools high up and forms clouds.' },
                    { label: 'Precipitation', detail: 'Water falls back to earth as rain.' },
                ],
            },
        },
        // The two activities preview as the task, not the reveal: that is the
        // slide a teacher is choosing, and the "-answers" half is generated
        // from these same fields rather than picked separately.
        'questions': {
            label: 'Questions',
            blurb: 'Questions for the class, plus an answer slide straight after.',
            fields: {
                heading: 'Check Your Understanding',
                instruction: 'Answer the questions in your book',
                items: [
                    { question: 'What drives evaporation from the oceans?', answer: 'Heat from the Sun.' },
                    { question: 'What happens to vapour as it rises and cools?', answer: 'It condenses into clouds.' },
                    { question: 'Name two forms precipitation can take.', answer: 'Rain and snow.' },
                ],
            },
        },
        'ordering': {
            label: 'Ordering',
            blurb: 'Shuffled steps for the class to order, plus the answer slide.',
            fields: {
                heading: 'Order the Water Cycle',
                instruction: 'Can you put these stages in the right order?',
                steps: [
                    'The Sun heats water in the oceans',
                    'Water evaporates and rises as vapour',
                    'Vapour cools and condenses into clouds',
                    'Water falls back to earth as precipitation',
                ],
                display_order: [2, 0, 3, 1],
            },
        },
        // The support ladder, most help first. Its fourth rung is Questions.
        'sentence-starters': {
            label: 'Sentence starters',
            blurb: 'One question, openings to start from and a word bank.',
            fields: {
                heading: 'Explain It in Your Own Words',
                question: 'Why does water vapour turn back into liquid as it rises?',
                starters: [
                    'As the vapour rises, the air around it…',
                    'This causes the water particles to…',
                    'We call this process…',
                ],
                word_bank: ['condensation', 'temperature', 'particles', 'droplets', 'cloud'],
            },
        },
        'structured-prompt': {
            label: 'Structured prompt',
            blurb: 'A longer question broken into guided steps.',
            fields: {
                heading: 'Build Your Answer',
                question: 'Explain how the water cycle moves water from the oceans to the land.',
                steps: [
                    { prompt: 'State where the energy comes from', starter: 'The cycle is driven by…' },
                    { prompt: 'Describe what happens to the water', starter: 'This causes the water to…' },
                    { prompt: 'Explain how it reaches the land', starter: '' },
                ],
            },
        },
        'worked-example': {
            label: 'Worked example',
            blurb: 'One problem solved step by step, revealed a click at a time.',
            fields: {
                heading: 'Calculating Evaporation Rate',
                problem: 'A pond loses 12 litres of water in 4 hours. What is the rate of evaporation?',
                steps: [
                    { step: 'Write the formula: rate = volume ÷ time', why: 'A rate is always an amount per unit of time' },
                    { step: 'Substitute: rate = 12 ÷ 4', why: 'Volume and time come straight from the question' },
                    { step: 'Calculate: 12 ÷ 4 = 3', why: 'Carry the units through to the answer' },
                ],
                answer: '3 litres per hour',
            },
        },
        'talk-task': {
            label: 'Talk task',
            blurb: 'Think, pair, share around one open question.',
            fields: {
                heading: 'What Do You Think?',
                prompt: 'Is the water you drank today older than the dinosaurs?',
                think: 'Decide yes or no, and why.',
                pair: 'Convince your partner – or be convinced.',
                share: 'Be ready to give one reason.',
            },
        },
        'true-false': {
            label: 'True or false',
            blurb: 'Statements the whole class answers at once, then the answers.',
            fields: {
                heading: 'True or False?',
                instruction: 'Thumbs up for true, thumbs down for false.',
                items: [
                    { statement: 'Clouds are made of tiny drops of water.', answer: true, why: 'Water vapour cools into droplets.' },
                    { statement: 'Puddles disappear because the ground drinks them.', answer: false, why: 'The water evaporates into the air.' },
                    { statement: 'Rain is water falling from clouds.', answer: true, why: 'Droplets join until they are heavy.' },
                ],
            },
        },
        'odd-one-out': {
            label: 'Odd one out',
            blurb: 'Four pictures – which one does not belong, and why?',
            fields: {
                heading: 'Odd One Out',
                instruction: 'Which one does not belong? Why?',
                items: [
                    { label: 'Rain', icon: 'cloud-rain' },
                    { label: 'Snow', icon: 'cloud-snow' },
                    { label: 'Sun', icon: 'sun' },
                    { label: 'Hail', icon: 'cloud' },
                ],
                odd: 2,
                reason: 'The sun is not water falling from the sky.',
            },
        },
        'spot-the-mistake': {
            label: 'Spot the mistake',
            blurb: 'A child\'s working with one slip in it for the class to fix.',
            fields: {
                heading: 'Spot the Mistake',
                name: 'Ella',
                intro: 'Ella worked out 34 + 25.',
                lines: ['30 + 20 = 50', '4 + 5 = 8', '50 + 8 = 58'],
                wrong: 1,
                fix: '4 + 5 = 9, so the answer is 59',
                why: 'Ella added the ones wrongly.',
            },
        },
        'multiple-choice': {
            label: 'Multiple choice',
            blurb: 'One question, lettered options built on real mistakes.',
            fields: {
                heading: 'Quick Check',
                question: 'What happens to water when it evaporates?',
                options: ['It disappears for good', 'It turns into water vapour', 'It soaks into the ground', 'It turns into ice'],
                answer: 1,
                explanation: 'The water becomes a gas in the air – it has not vanished.',
            },
        },
        'word-cards': {
            label: 'Word cards',
            blurb: 'Big words read aloud together, one per click.',
            fields: {
                heading: 'Read With Me',
                instruction: 'My turn, your turn!',
                words: [{ word: 'rain', mark: 'ai' }, { word: 'snail', mark: 'ai' }, { word: 'train', mark: 'ai' }, { word: 'paint', mark: 'ai' }],
            },
        },
        'maths-picture': {
            label: 'Maths picture',
            blurb: 'A drawn ten frame, number line or bar model with a question.',
            fields: {
                heading: 'How Many?',
                picture: { kind: 'ten-frame', a: 7, b: 0 },
                question: 'How many more make 10?',
                answer: '3 more',
            },
        },
    };

    function fieldsFor(name) { return SAMPLES[name] ? SAMPLES[name].fields : null; }
    function labelFor(name) { return SAMPLES[name] ? SAMPLES[name].label : name; }
    function blurbFor(name) { return SAMPLES[name] ? SAMPLES[name].blurb : ''; }
    function names() { return Object.keys(SAMPLES); }

    return { SAMPLES, fieldsFor, labelFor, blurbFor, names };
}));
